import React from "react";
import { HiChevronUp, HiChevronDown } from "react-icons/hi";
import Step from "../Step";
import styles from "../styles.module.css";
import WithUserForm from "../../Register/WithUserForm";
import CTAButton from "../../utils/Button/CTAButton";

const GENDERS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
];

// Scroll-wheel date picker (Day · Month · Year)
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WHEEL_ITEM_H = 34;
const WHEEL_VISIBLE = 5; // odd number so one row sits in the centre
const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const pad2 = (n) => String(n).padStart(2, "0");

// Parse a saved DOB into { year, month (0-based), day }, or null.
const parseDob = (raw) => {
  if (!raw) return null;
  const s = String(raw);
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return { year: +iso[1], month: +iso[2] - 1, day: +iso[3] };
  const d = /^\d+$/.test(s) ? new Date(Number(s)) : new Date(s);
  if (isNaN(d.getTime())) return null;
  return { year: d.getUTCFullYear(), month: d.getUTCMonth(), day: d.getUTCDate() };
};

// Minimum age, enforced by year, anyone born that year or earlier can proceed.
const MIN_AGE = 18;

function WheelColumn({ values, index, onChange, flex = 1 }) {
  const ref = React.useRef(null);
  const settle = React.useRef(null);
  const selfChange = React.useRef(false);
  const height = WHEEL_ITEM_H * WHEEL_VISIBLE;
  const edgePad = (height - WHEEL_ITEM_H) / 2;

  // Keep the column scrolled to the selected row. Skip when the change came
  // from this column's own scroll (selfChange) so we don't fight the user;
  // still react when the parent moves us (e.g. day clamped after a month change).
  React.useEffect(() => {
    if (selfChange.current) {
      selfChange.current = false;
      return;
    }
    const el = ref.current;
    if (el) el.scrollTop = index * WHEEL_ITEM_H;
  }, [index]);

  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    clearTimeout(settle.current);
    settle.current = setTimeout(() => {
      let i = Math.round(el.scrollTop / WHEEL_ITEM_H);
      i = Math.max(0, Math.min(values.length - 1, i));
      const snapped = i * WHEEL_ITEM_H;
      if (Math.abs(el.scrollTop - snapped) > 1) {
        el.scrollTo({ top: snapped, behavior: "smooth" });
      }
      if (i !== index) {
        selfChange.current = true;
        onChange(i);
      }
    }, 90);
  };

  // Desktop/tablet only: step one row per click (mobile just scrolls).
  const step = (delta) => {
    const next = index + delta;
    if (next >= 0 && next < values.length) onChange(next);
  };
  const arrowStyle = {
    position: "absolute",
    left: 0,
    right: 0,
    height: WHEEL_ITEM_H,
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    color: "rgba(176,141,87,0.9)",
    cursor: "pointer",
    padding: 0,
    zIndex: 3,
  };

  return (
    <div style={{ position: "relative", flex, height }}>
      <button
        type="button"
        aria-label="Previous"
        className={styles.wheel_arrow}
        onClick={() => step(-1)}
        style={{ ...arrowStyle, top: 0, opacity: index > 0 ? 1 : 0.25 }}
      >
        <HiChevronUp size={20} />
      </button>
      <div
        ref={ref}
        onScroll={onScroll}
        className={styles.wheel_scroll_col}
        style={{
          width: "100%",
          height: "100%",
          overflowY: "scroll",
          scrollSnapType: "y mandatory",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{ height: edgePad }} />
        {values.map((v, i) => (
        <div
          key={i}
          style={{
            height: WHEEL_ITEM_H,
            lineHeight: `${WHEEL_ITEM_H}px`,
            textAlign: "center",
            scrollSnapAlign: "center",
            color: i === index ? "#B08D57" : "rgba(255,255,255,0.4)",
            fontWeight: i === index ? 700 : 400,
            fontSize: i === index ? "18px" : "15px",
            transition: "color .15s ease, font-size .15s ease",
          }}
        >
          {v}
        </div>
      ))}
        <div style={{ height: edgePad }} />
      </div>
      <button
        type="button"
        aria-label="Next"
        className={styles.wheel_arrow}
        onClick={() => step(1)}
        style={{ ...arrowStyle, bottom: 0, opacity: index < values.length - 1 ? 1 : 0.25 }}
      >
        <HiChevronDown size={20} />
      </button>
    </div>
  );
}

function WheelDatePicker({ value, onChange }) {
  // Newest date you can pick is exactly 18 years ago (year, then month, then day).
  const now = new Date();
  const maxYear = now.getFullYear() - MIN_AGE;
  const maxMonth = now.getMonth();
  const maxDay = now.getDate();
  const minYear = maxYear - 90;
  const years = [];
  for (let y = maxYear; y >= minYear; y--) years.push(y); // newest at top

  // In the newest year, hide months that haven't happened yet; same for days.
  const atMaxYear = value.year === maxYear;
  const monthCount = atMaxYear ? maxMonth + 1 : 12;
  const months = MONTHS.slice(0, monthCount);

  const dayLimit = daysInMonth(value.year, value.month);
  const dim = atMaxYear && value.month === maxMonth ? Math.min(dayLimit, maxDay) : dayLimit;
  const days = [];
  for (let d = 1; d <= dim; d++) days.push(d);

  const clampDay = (year, month, day) => {
    let max = daysInMonth(year, month);
    if (year === maxYear && month === maxMonth) max = Math.min(max, maxDay);
    return Math.min(day, max);
  };

  const setDay = (i) => onChange({ ...value, day: days[i] });
  const setMonth = (i) => onChange({ ...value, month: i, day: clampDay(value.year, i, value.day) });
  const setYear = (i) => {
    const year = years[i];
    const month = year === maxYear ? Math.min(value.month, maxMonth) : value.month;
    onChange({ ...value, year, month, day: clampDay(year, month, value.day) });
  };

  const height = WHEEL_ITEM_H * WHEEL_VISIBLE;
  const edgePad = (height - WHEEL_ITEM_H) / 2;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        background: "#000",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex" }}>
        <WheelColumn values={days} index={value.day - 1} onChange={setDay} flex={1} />
        <WheelColumn values={months} index={value.month} onChange={setMonth} flex={1.2} />
        <WheelColumn values={years} index={maxYear - value.year} onChange={setYear} flex={1.2} />
      </div>
      <div
        style={{
          position: "absolute",
          top: edgePad,
          left: 0,
          right: 0,
          height: "1px",
          background: "linear-gradient(to right, rgba(176,141,87,0), rgba(176,141,87,0.9) 12%, rgba(176,141,87,0.9) 88%, rgba(176,141,87,0))",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: edgePad + WHEEL_ITEM_H - 1,
          left: 0,
          right: 0,
          height: "1px",
          background: "linear-gradient(to right, rgba(176,141,87,0), rgba(176,141,87,0.9) 12%, rgba(176,141,87,0.9) 88%, rgba(176,141,87,0))",
          pointerEvents: "none",
        }}
      />
      {/* top / bottom fade for the wheel effect */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: edgePad,
          background: "linear-gradient(to bottom, #000, rgba(0,0,0,0))",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: edgePad,
          background: "linear-gradient(to top, #000, rgba(0,0,0,0))",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

export default class StepAboutYou extends React.Component {
  constructor(props) {
    super(props);
    this.initialized = false;
    // Default DOB is a random date (12 Nov 2006), The user must still
    // interact with the wheel (dobTouched) before Next enables, so
    // the default is never submitted blindly.
    this.state = {
      gender: "",
      dob: { year: 2006, month: 10, day: 12 },
      dobTouched: false,
    };
  }

  initFromUser = (currentUser) => {
    if (this.initialized || !currentUser) return;
    this.initialized = true;
    const next = { gender: currentUser.gender || "" };
    // Seed the wheel from the saved DOB if present, and treat it as already chosen.
    const parsed = parseDob(currentUser.dateOfBirth);
    if (parsed) {
      next.dob = parsed;
      next.dobTouched = true;
    }
    this.setState(next);
  };

  async onSubmit(values, _, props) {
    const { gender, dob } = this.state;
    if (!gender) return;
    const dateOfBirth = `${dob.year}-${pad2(dob.month + 1)}-${pad2(dob.day)}`;
    const input = WithUserForm.formatUserAboutInput({ id: values.id, gender, dateOfBirth });
    await props.updateUserAbout({ variables: { input } });
    return props.machine.service.send({ type: "NEXT" });
  }

  render() {
    const { gender, dob, dobTouched } = this.state;
    const nextDisabled = !gender || !dobTouched;
    return (
      <WithUserForm onSubmit={(values, _, props) => this.onSubmit(values, _, props)}>
        {({ form: { handleSubmit }, userQuery: { data } }) => {
          this.initFromUser(data?.currentUser);
          const footer = (
            <div
              style={{
                width: "90vw",
                maxWidth: "400px",
                minHeight: "150px",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-end",
                paddingBottom: "10px",
              }}
            >
              <div style={{ width: "100%", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CTAButton onClick={handleSubmit} disabled={nextDisabled} text={"Next"} />
              </div>
              <p className={` ${styles.default_text_size}`} style={{ color: "#B08D57", lineHeight: 0.9 }}>
                Having trouble? <a href="https://wa.me/443330166335" style={{ textDecoration: "underline", fontWeight: "bold" }}>Contact us</a>.
              </p>
            </div>
          );
          return (
            <Step footer={footer} scrollable>
              <p
                className="text-white text-md"
                style={{ fontWeight: 700, fontSize: "clamp(1px, 4vw, 18px)", marginTop: "50px" }}
              >
                Tell us more about you
              </p>
              <p className={styles.sub_title}>
                We need this information to allow you to create or accept jobs, to open a store and to receive payments
              </p>
              <form onSubmit={handleSubmit} className={styles.form_step_skills}>
                <div className={styles.skills_categories}>
                  <p className={styles.section_label}>Gender</p>
                  <div className={styles.skill_buttons_grid}>
                    {GENDERS.map((g) => (
                      <button
                        key={g.value}
                        type="button"
                        className={`${styles.tag} ${gender === g.value ? styles.skill_button_selected : ""}`}
                        onClick={() => this.setState({ gender: g.value })}
                      >
                        {g.label}
                      </button>
                    ))}
                  </div>
                  <p className={styles.section_label} style={{ marginTop: "3vh", marginBottom: 0 }}>Date of birth</p>
                  <WheelDatePicker value={dob} onChange={(v) => this.setState({ dob: v, dobTouched: true })} />
                </div>
              </form>
            </Step>
          );
        }}
      </WithUserForm>
    );
  }
}
